import { __decorate } from 'tslib';
// -----------------------------------------------------------------------------------------------------------------
// Restricted - Copyright (C) Siemens Healthineers AG 2023.
// -----------------------------------------------------------------------------------------------------------------
import { html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { ShuiLitElement } from '../../utils/event-decorator.js';
import { keyboardInteraction } from '../../utils/keyboardInteraction.js';
import { KEYCODE } from '../../utils/keycode.js';
import listStyles from './sh-list.lit.scss.js';
let SHList = class SHList extends ShuiLitElement {
  constructor() {
    super(...arguments);
    // Track if focus was triggered by pointer
    this.focusByPointer = false;
    this.onDocumentPointerDown = () => {
      // If sh-list is focused and user clicks anywhere, blur it to remove outline
      if (document.activeElement === this) {
        this.blur();
      }
    };
    this.onPointerDownPreventFocus = (event) => {
      let target = event.target;
      while (target && target !== this) {
        if (target.tagName === 'SH-LIST-ITEM') {
          // Only prevent default if not draggable
          if (!target.hasAttribute('draggable') || target.getAttribute('draggable') === 'false') {
            event.preventDefault();
          }
          break;
        }
        target = target.parentElement;
      }
    };
    this.virtualFocusIndex = -1;
    /** If set to `true`, the list will have no background color nor elevation applied to it. */
    this.flat = false;
    /** Defines the padding of the list items placed inside */
    this.integrated = false;
    /** Defines the selectable state of the list items. Possible values are `multiple` and `single` */
    this.selectionType = 'multiple';
    /** role of the list, used for accessibility purposes. */
    this.role = 'list';
    this.boundHandleFocus = this.handleFocus.bind(this);
    this.boundHandleBlur = this.handleBlur.bind(this);
    this.slotChangeHandler = () => this.updateRole();
  }
  /** Activates only the given item, deactivates all others. */
  activateSingleItem(listItems, itemToActivate) {
    listItems.forEach((item) => item.removeAttribute('active'));
    itemToActivate.setAttribute('active', 'true');
  }
  static get styles() {
    return [listStyles];
  }
  render() {
    return html` <slot></slot> `;
  }
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', (e) => {
      this.focusByPointer = false;
      this.handleKeydown(e);
    });
    this.addEventListener(
      'pointerdown',
      () => {
        this.focusByPointer = true;
      },
      true
    );
    this.addEventListener('focus', this.boundHandleFocus);
    this.addEventListener('blur', this.boundHandleBlur);
    this.addEventListener('pointerdown', this.onPointerDownPreventFocus, true);
    document.addEventListener('pointerdown', this.onDocumentPointerDown, true);
  }
  getSlottedListItems() {
    // Get all slotted sh-list-item elements
    return this.slotEl
      ? this.slotEl
          .assignedElements({ flatten: true })
          .filter((el) => el.tagName === 'SH-LIST-ITEM')
      : [];
  }
  handleBlur() {
    // Remove .focused class from all items when list loses focus
    const items = this.getSlottedListItems();
    items.forEach((item) => item.classList.remove('focused'));
  }
  handleFocus() {
    // Only run focus logic if focus was triggered by keyboard
    if (this.focusByPointer) {
      this.focusByPointer = false;
      return;
    }
    const items = this.getSlottedListItems();
    // If this list is a plain list (not interactive), do not focus any item on initial focus
    if (this.getAttribute('role') === 'list') {
      items.forEach((item) => item.classList.remove('focused'));
      this.removeAttribute('aria-activedescendant');
      return;
    }
    // Remove all focused classes
    items.forEach((item) => item.classList.remove('focused'));
    // Only focus for listbox/option pattern
    if (this.getAttribute('role') === 'listbox') {
      const optionItems = items.filter(
        (item) => item.getAttribute('role') === 'option' && !item.hasAttribute('disabled')
      );
      if (!optionItems.length) return;
      // Find first selected non-disabled option
      let focusIndex = optionItems.findIndex(
        (item) => item.hasAttribute('active') && item.getAttribute('active') !== 'false'
      );
      if (focusIndex === -1) {
        // If none selected, focus first non-disabled option
        focusIndex = 0;
      }
      this.virtualFocusIndex = focusIndex;
      const focusItem = optionItems[focusIndex];
      if (focusItem) {
        focusItem.classList.add('focused');
        this.setAttribute('aria-activedescendant', focusItem.id);
        if (this.selectionType === 'single' && !focusItem.hasAttribute('active')) {
          focusItem.setAttribute('active', 'true');
        }
      }
    }
  }
  firstUpdated() {
    this.updateRole();
    // Listen for slot changes to update role dynamically
    if (this.slotEl) {
      this.slotEl.addEventListener('slotchange', this.slotChangeHandler);
    }
  }
  updateRole() {
    const items = this.getSlottedListItems();
    const hasHref = items.some((item) => item.href);
    const allHref = items.length > 0 && items.every((item) => item.href);
    const allDisabled = items.length > 0 && items.every((item) => item.disabled);
    const isSelectableItems = items.some((item) => item.role === 'option' || item.toggle);
    const isInteractive = items.some((item) => item.clickable || item.role === 'option');
    // If all items are disabled, list should be non-focusable
    if (allDisabled) {
      this.removeAttribute('tabindex');
    } else if (allHref) {
      this.removeAttribute('tabindex');
    } else if (isInteractive || isSelectableItems) {
      this.setAttribute('tabindex', '0');
    } else {
      this.removeAttribute('tabindex');
    }
    if (hasHref) {
      this.setAttribute('role', 'navigation');
      this.removeAttribute('aria-multiselectable');
    } else if (isSelectableItems) {
      this.setAttribute('role', 'listbox');
      this.setAttribute(
        'aria-multiselectable',
        'multiple' === this.selectionType ? 'true' : 'false'
      );
    } else {
      this.setAttribute('role', 'list');
      this.removeAttribute('aria-multiselectable');
    }
  }
  handleKeydown(e) {
    // Do not intercept Tab – let browser handle it
    if (keyboardInteraction(e, KEYCODE.TAB)) return;
    // If this list is a plain list (not interactive), only allow navigation/focus for clickable items
    let focusableItems;
    if (this.getAttribute('role') === 'list') {
      focusableItems = Array.from(
        this.querySelectorAll('sh-list-item[clickable]:not([disabled])')
      ).filter((item) => !item.href);
    } else {
      focusableItems = Array.from(this.querySelectorAll('sh-list-item:not([disabled])')).filter(
        (item) => item.getAttribute('role') !== 'presentation' && !item.href
      );
    }
    if (!focusableItems.length) return;
    // Only handle navigation keys for custom focus
    let navigationKey = false;
    if (keyboardInteraction(e, KEYCODE.ARROW_DOWN)) {
      e.preventDefault();
      navigationKey = true;
      if (this.virtualFocusIndex < focusableItems.length - 1) {
        this.virtualFocusIndex++;
      } else {
        this.virtualFocusIndex = 0;
      }
    } else if (keyboardInteraction(e, KEYCODE.ARROW_UP)) {
      e.preventDefault();
      navigationKey = true;
      if (this.virtualFocusIndex > 0) {
        this.virtualFocusIndex--;
      } else {
        this.virtualFocusIndex = focusableItems.length - 1;
      }
    } else if (e.key === 'Home') {
      e.preventDefault();
      navigationKey = true;
      this.virtualFocusIndex = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      navigationKey = true;
      this.virtualFocusIndex = focusableItems.length - 1;
    }
    // Always define focusedItem for use in Space key handler
    const focusedItem = focusableItems[this.virtualFocusIndex];
    if (navigationKey) {
      // Remove all focused classes
      focusableItems.forEach((item) => item.classList.remove('focused'));
      // Clamp index if needed
      if (this.virtualFocusIndex < 0) {
        this.virtualFocusIndex = 0;
      }
      if (this.virtualFocusIndex > focusableItems.length - 1) {
        this.virtualFocusIndex = focusableItems.length - 1;
      }
      // Add focused class to the current item
      if (focusedItem) {
        focusedItem.classList.add('focused');
        this.setAttribute('aria-activedescendant', focusedItem.id);
        if (this.selectionType === 'single') {
          this.activateSingleItem(focusableItems, focusedItem);
        }
        // Move focus to sh-list if a sh-list-item would otherwise receive focus
        if (document.activeElement !== this) {
          this.focus();
        }
      }
    }
    // Space key triggers click on the focused item
    if (keyboardInteraction(e, KEYCODE.SPACE)) {
      e.preventDefault();
      if (focusedItem) focusedItem.click();
    }
  }
  disconnectedCallback() {
    this.removeEventListener('keydown', this.handleKeydown);
    this.removeEventListener('focus', this.boundHandleFocus);
    this.removeEventListener('blur', this.boundHandleBlur);
    this.removeEventListener('pointerdown', this.onPointerDownPreventFocus, true);
    document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
    if (this.slotEl) {
      this.slotEl.removeEventListener('slotchange', this.slotChangeHandler);
    }
    super.disconnectedCallback();
  }
};
__decorate([property({ type: Boolean, reflect: true })], SHList.prototype, 'flat', void 0);
__decorate([property({ type: Boolean, reflect: true })], SHList.prototype, 'integrated', void 0);
__decorate(
  [property({ type: String, reflect: true, attribute: 'selection-type' })],
  SHList.prototype,
  'selectionType',
  void 0
);
__decorate([property({ type: String, reflect: true })], SHList.prototype, 'role', void 0);
__decorate([query('slot')], SHList.prototype, 'slotEl', void 0);
SHList = __decorate([customElement('sh-list')], SHList);
export { SHList };
